import { type Brand, make } from "ts-brand";
import { z } from "zod";

import { MandateId, PersonId } from "./mandate";
import { type TaskItem, type TaskItemId, type TaskList, TaskItemSchema, TaskListId } from "./task-list";

/**
 * TaskSchedule (Aufgabenplan) domain model (Core 2 — S6 Follow-up). A
 * TaskSchedule turns a one-off TaskList into a recurring request: Trexpert
 * creates it from an existing TaskList, picks a {@link Cadence} (Rhythmus) and a
 * Versandtag (the day on which each occurrence is sent, expressed as `sendDay` +
 * `monthsAfterPeriodEnd`), and the schedule then drives the periodic generation
 * of fresh TaskLists. Each occurrence's concrete Versanddatum is derived by
 * {@link deriveSendDate}: the list is sent on exactly that day — there is no
 * lead time and no separate deadline. This module covers the *creation* slice
 * and the derivation contract; the `TaskScheduler` generation job is a follow-up
 * (see docs/stories/impl-task-schedule). The positions are copied from the
 * source list as a template snapshot — always `open`, with no closure carried
 * over. See docs/glossar.md (Aufgabenplan / Rhythmus / Versandtag). Nominal IDs
 * via ts-brand; Zod validates at the boundary.
 */

// --- Branded ID ---

export type TaskScheduleId = Brand<string, "TaskScheduleId">;
export const TaskScheduleId = make<TaskScheduleId>();

// --- Cadence (Rhythmus) ---

export const CADENCES = ["monthly", "quarterly", "semiannual", "yearly"] as const;
export type Cadence = (typeof CADENCES)[number];
export const CadenceSchema = z.enum(CADENCES);

/** Number of months in one period of a cadence. */
const CADENCE_PERIOD_MONTHS: Readonly<Record<Cadence, number>> = {
    monthly: 1,
    quarterly: 3,
    semiannual: 6,
    yearly: 12,
};

// --- Status ---

/** Reversible (unlike a TaskList): a schedule can be paused and resumed. */
export const TASK_SCHEDULE_STATUSES = ["active", "paused"] as const;
export type TaskScheduleStatus = (typeof TASK_SCHEDULE_STATUSES)[number];
export const TaskScheduleStatusSchema = z.enum(TASK_SCHEDULE_STATUSES);

// --- Aggregate ---

export const TaskScheduleSchema = z.object({
    id: z.string().min(1).transform(TaskScheduleId),
    mandateId: z.string().min(1).transform(MandateId),
    title: z.string().min(1),
    /** The TaskList this schedule was created from (its positions are snapshotted). */
    sourceTaskListId: z.string().min(1).transform(TaskListId),
    cadence: CadenceSchema,
    /**
     * Versandtag (send day): day of the month, 1–31. For `monthly` it is the day
     * within the occurrence's month; for longer cadences it is the day within the
     * month `monthsAfterPeriodEnd` months after the period closes. The concrete
     * Versanddatum is derived by {@link deriveSendDate}.
     */
    sendDay: z.number().int().min(1).max(31),
    /**
     * Months after the covered period's end on which the Versandtag falls. `0` for
     * `monthly` (the day lies within the period itself); ≥1 for cadences sent after
     * the period (e.g. MWST ≈ 1–2, Jahresabschluss ≈ 3).
     */
    monthsAfterPeriodEnd: z.number().int().min(0).max(12).default(0),
    status: TaskScheduleStatusSchema,
    items: z.array(TaskItemSchema),
    createdAt: z.string().min(1),
    createdBy: z.string().min(1).transform(PersonId),
    /**
     * Idempotency marker: the Versanddatum (`YYYY-MM-DD`) of the most recent
     * occurrence the `TaskScheduler` already materialised. The job skips a
     * schedule whose due send date equals this value, so re-runs on the same day
     * never produce a duplicate. Absent until the first occurrence is generated.
     */
    lastGeneratedSendDate: z.string().optional(),
});
export type TaskSchedule = z.infer<typeof TaskScheduleSchema>;

// --- Input schemas ---

export const CreateTaskScheduleInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    sourceTaskListId: z.string().min(1).transform(TaskListId),
    cadence: CadenceSchema,
    sendDay: z.number().int().min(1).max(31),
    monthsAfterPeriodEnd: z.number().int().min(0).max(12).default(0),
});
export type CreateTaskScheduleInput = z.infer<typeof CreateTaskScheduleInputSchema>;

export const UpdateTaskScheduleInputSchema = z.object({
    scheduleId: z.string().min(1).transform(TaskScheduleId),
    title: z.string().min(1),
    cadence: CadenceSchema,
    sendDay: z.number().int().min(1).max(31),
    monthsAfterPeriodEnd: z.number().int().min(0).max(12).default(0),
});
export type UpdateTaskScheduleInput = z.infer<typeof UpdateTaskScheduleInputSchema>;

// --- Domain error ---

export class TaskScheduleError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "TaskScheduleError";
    }
}

/**
 * Copy a source TaskItem into a fresh template position: always `open`, with any
 * prior closure (Document link / Remark) dropped — a schedule describes the
 * recurring *request*, not a past fulfilment.
 */
const asTemplateItem = (item: TaskItem): TaskItem =>
    item.kind === "receipt"
        ? {
              id: item.id,
              kind: "receipt",
              date: item.date,
              description: item.description,
              amount: item.amount,
              currency: item.currency,
              status: "open",
          }
        : {
              id: item.id,
              kind: "freeform",
              name: item.name,
              description: item.description,
              status: "open",
          };

/**
 * Materialise a fresh `drafted` TaskList from a schedule's template for one
 * occurrence. Every item gets a **brand-new** id from `meta.newItemId` (never the
 * source/template id) and starts `open` with no closure, and the list carries a
 * **brand-new** `meta.listId` plus `generatedFromScheduleId` provenance. Minting
 * fresh ids per occurrence is the safety property: because the repository upserts
 * by id, a reused id would overwrite (and re-open) an earlier list — so each
 * occurrence is a distinct list. The caller (TaskScheduler) sends it and runs the
 * dispatch side effects; this function stays pure (no clock/crypto).
 */
export const materializeTaskListFromSchedule = (
    schedule: TaskSchedule,
    meta: { listId: TaskListId; newItemId: () => TaskItemId; at: string }
): TaskList => ({
    id: meta.listId,
    mandateId: schedule.mandateId,
    title: schedule.title,
    status: "drafted",
    items: schedule.items.map(asTemplateItem).map(item => ({ ...item, id: meta.newItemId() })),
    generatedFromScheduleId: schedule.id,
    createdAt: meta.at,
    updatedAt: meta.at,
});

/** Update the mutable fields (title, cadence, Versandtag inputs) of a TaskSchedule. */
export const applyUpdateTaskSchedule = (
    schedule: TaskSchedule,
    input: Pick<UpdateTaskScheduleInput, "title" | "cadence" | "sendDay" | "monthsAfterPeriodEnd">
): TaskSchedule => ({
    ...schedule,
    title: input.title,
    cadence: input.cadence,
    sendDay: input.sendDay,
    monthsAfterPeriodEnd: input.monthsAfterPeriodEnd,
});

/** Pause an active TaskSchedule — no new TaskLists are generated while paused. */
export const applyPauseTaskSchedule = (schedule: TaskSchedule): TaskSchedule => ({
    ...schedule,
    status: "paused",
});

/** Resume a paused TaskSchedule — generation resumes at the next send date. */
export const applyResumeTaskSchedule = (schedule: TaskSchedule): TaskSchedule => ({
    ...schedule,
    status: "active",
});

/**
 * Record that the occurrence due on `sendDate` (`YYYY-MM-DD`) has been
 * materialised — the idempotency marker the `TaskScheduler` stamps after it
 * generates and saves a TaskList, so a same-day re-run is skipped.
 */
export const applyMarkGenerated = (schedule: TaskSchedule, sendDate: string): TaskSchedule => ({
    ...schedule,
    lastGeneratedSendDate: sendDate,
});

/**
 * Create an `active` TaskSchedule from a source TaskList: copies the list title
 * and its positions (as open template items), and stamps the cadence + Versandtag.
 */
export const applyCreateTaskSchedule = (
    sourceList: TaskList,
    input: { cadence: Cadence; sendDay: number; monthsAfterPeriodEnd?: number },
    meta: { id: TaskScheduleId; at: string; by: PersonId }
): TaskSchedule => ({
    id: meta.id,
    mandateId: sourceList.mandateId,
    title: sourceList.title,
    sourceTaskListId: sourceList.id,
    cadence: input.cadence,
    sendDay: input.sendDay,
    monthsAfterPeriodEnd: input.monthsAfterPeriodEnd ?? 0,
    status: "active",
    items: sourceList.items.map(asTemplateItem),
    createdAt: meta.at,
    createdBy: meta.by,
});

// --- Versanddatum derivation (Versandtag → concrete send date) ---

/** Number of days in a given month (1–12), honouring leap years. */
const daysInMonth = (year: number, month1to12: number): number =>
    new Date(Date.UTC(year, month1to12, 0)).getUTCDate();

/** Zero-padded ISO calendar date `YYYY-MM-DD` (no time component). */
const isoDate = (year: number, month1to12: number, day: number): string =>
    `${String(year).padStart(4, "0")}-${String(month1to12).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/**
 * The last calendar month (1–12) of the cadence period that contains `month`.
 * monthly → that month; quarterly → 3/6/9/12; semiannual → 6/12; yearly → 12.
 */
export const periodEndMonth = (cadence: Cadence, month1to12: number): number => {
    const span = CADENCE_PERIOD_MONTHS[cadence];
    return Math.ceil(month1to12 / span) * span;
};

/**
 * Derive the concrete Versanddatum (send date) for the occurrence whose period
 * contains `reference`. Anchored to the period's last month, shifted by
 * `monthsAfterPeriodEnd`, on day `sendDay` clamped to that month's length (so
 * "31" becomes 28/29 in February). Returns an ISO date `YYYY-MM-DD`.
 *
 * This is the contract the deferred `TaskScheduler` generation job uses to decide
 * when to materialise and send each generated TaskList — there is no lead time
 * and no separate deadline.
 */
export const deriveSendDate = (
    input: { cadence: Cadence; sendDay: number; monthsAfterPeriodEnd: number },
    reference: Date
): string => {
    const refYear = reference.getUTCFullYear();
    const refMonth = reference.getUTCMonth() + 1; // 1–12
    const endMonth = periodEndMonth(input.cadence, refMonth);
    const shifted = endMonth - 1 + input.monthsAfterPeriodEnd; // zero-based, may exceed 11
    const year = refYear + Math.floor(shifted / 12);
    const month = (shifted % 12) + 1;
    const day = Math.min(input.sendDay, daysInMonth(year, month));
    return isoDate(year, month, day);
};

/** The calendar date (`YYYY-MM-DD`, UTC) of an instant — ignoring the time of day. */
const referenceIsoDate = (reference: Date): string =>
    isoDate(reference.getUTCFullYear(), reference.getUTCMonth() + 1, reference.getUTCDate());

/**
 * Decide whether an `active` schedule is due to generate on `reference`, and if
 * so return the occurrence's Versanddatum (`YYYY-MM-DD`); otherwise `undefined`.
 *
 * Due ⇔ the schedule is `active`, the reference's calendar date *equals* the
 * derived send date for its period, that send date has not already been generated
 * ({@link applyMarkGenerated}'s marker), and the plan was created *before* that
 * send date — a plan created on its own Versandtag waits for the next cycle, so
 * dispatch never hinges on whether creation beat the daily sweep. Exact-day match
 * + a daily job
 * fires each occurrence exactly once; the marker guards same-day re-runs. A run
 * that skips entirely over a send day (e.g. an outage spanning the Versandtag)
 * misses that occurrence by design — catch-up across a period boundary is out of
 * scope for this slice.
 */
export const isDueForGeneration = (schedule: TaskSchedule, reference: Date): string | undefined => {
    if (schedule.status !== "active") {
        return undefined;
    }
    const sendDate = deriveSendDate(schedule, reference);
    if (referenceIsoDate(reference) !== sendDate) {
        return undefined;
    }
    if (schedule.lastGeneratedSendDate === sendDate) {
        return undefined;
    }
    // A plan created on (or after) the occurrence's Versandtag does not fire that
    // day: that period is already under way. Otherwise whether a plan created on
    // its Versandtag dispatched "today" would hinge on whether creation beat the
    // daily 06:00 sweep — instead, the first dispatch is the next cycle.
    if (referenceIsoDate(new Date(schedule.createdAt)) >= sendDate) {
        return undefined;
    }
    return sendDate;
};
